/**
 * Interview Container — the founder-facing scoring-interview conversation.
 *
 * A warm mentor chat (NOT a form): the founder starts or resumes a natural
 * conversation, answers one question at a time, and can wrap up when ready.
 * Built on 88-01's data layer (`useInterview`): start-on-mount transparently
 * RESUMES an active session (the edge fn returns the full transcript), so a
 * returning founder continues mid-conversation with no extra wiring.
 *
 * Adapted from the startup-insights chat (message list + composer structure)
 * but theme-aware (light + dark) and teal-accented to read as a distinct
 * mentor conversation rather than the amber insights advisor.
 *
 * SLOTS for later plans (clearly marked below):
 *   • `renderCompletion` — 88-05 completion reveal (polls the session for the
 *     before/after uplift; container hands it the `sessionId`).
 *   • `renderVoiceControl` — 88-06 mic capture (transcript → composer).
 */

import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  Pressable,
  Keyboard,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  FadeInUp,
  FadeIn,
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  withSpring,
  Easing,
} from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import {
  Send,
  Sparkles,
  Check,
  RotateCcw,
  ChevronLeft,
  MessagesSquare,
} from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { getUserMessage } from '@/lib/errors';
import { isWeb } from '@/lib/platform';
import { useThemeColors } from '@/lib/useThemeColors';
import { useInterview } from '@/lib/interview/use-interview';
import type {
  InterviewMessage as InterviewMessageType,
  InterviewDigestResponse,
} from '@/lib/interview/types';
import { InterviewProgress } from './InterviewProgress';
import { InterviewCompletion } from './InterviewCompletion';
import { InterviewDigestReview } from './InterviewDigestReview';
import { InterviewVoiceButton } from './InterviewVoiceButton';
import { canWrapUp, shouldShowWrapUp } from './interview-exit';
import { coveragePercent, coverageLabel } from './interview-progress-bar';

/**
 * Voice-input kill-switch (Phase 88). Voice ships DARK by default: the code is
 * present in every build, but the mic is only rendered when
 * `EXPO_PUBLIC_VOICE_ENABLED === 'true'`. Production leaves it unset, so voice
 * stays OFF until the DPO signs DPIA Addendum A1 (zero-custody voice). Set it in
 * `.env.local` to exercise voice locally. See `.planning/compliance/dpia.md` §A1.
 */
const VOICE_ENABLED = process.env.EXPO_PUBLIC_VOICE_ENABLED === 'true';

export interface InterviewContainerProps {
  /** Startup the founder owns — required to start/resume the interview. */
  startupId: string;
  /** Founder-friendly startup name for warm copy. */
  startupName?: string;
  /** Header title (founder-facing). Defaults to the interview's standard copy. */
  title?: string;
  onClose?: () => void;
  /**
   * === COMPLETION SLOT (88-05 wires here) ===
   * Rendered as a full overlay once the founder wraps up. Receives the
   * `sessionId` to poll (`useInterviewSession`) for the before/after uplift and
   * an `onDone` to dismiss + reset. When absent, a default confirmation shows.
   */
  renderCompletion?: (ctx: { sessionId: string; onDone: () => void }) => React.ReactNode;
  /**
   * === VOICE SLOT (88-06 wires here) ===
   * Rendered in the composer. Wire mic → transcript → `onTranscript` (drops the
   * text into the composer for the founder to review, then send).
   */
  renderVoiceControl?: (ctx: {
    onTranscript: (text: string) => void;
    disabled: boolean;
  }) => React.ReactNode;
}

function InterviewContainerComponent({
  startupId,
  startupName,
  title = 'Tell us about your startup',
  onClose,
  renderCompletion,
  renderVoiceControl,
}: InterviewContainerProps) {
  const colors = useThemeColors();
  const [inputValue, setInputValue] = useState('');
  const [wrappedUp, setWrappedUp] = useState(false);
  // Wrap-up now routes through a digest review step (Task 2.3) BEFORE completing:
  // `reviewing` shows the review overlay; `digestData` holds the generated digest
  // (null while it is being generated or if generation failed).
  const [reviewing, setReviewing] = useState(false);
  const [digestData, setDigestData] = useState<InterviewDigestResponse | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  const {
    sessionId,
    messages,
    start,
    send,
    complete,
    digest,
    reset,
    overallCoverage,
    dimensionCoverage,
  } = useInterview();

  // Start (or transparently RESUME) the conversation once on mount. The edge fn
  // returns the full prior transcript when an active session exists, so a
  // returning founder continues mid-conversation with no extra plumbing.
  const startedRef = useRef(false);
  const startMutate = start.mutate;
  useEffect(() => {
    if (!startedRef.current && startupId && !sessionId) {
      startedRef.current = true;
      startMutate({ startupId });
    }
  }, [startupId, sessionId, startMutate]);

  // Freshest founder-friendly coverage: latest turn's labels, falling back to
  // the opener's. Never dimension ids (88-01 trade-secret contract).
  const coveredTopics = send.data?.coveredTopics ?? start.data?.gapsCoveredLabels ?? [];

  const isThinking = start.isPending || send.isPending;
  // "Wrap up" (complete + rescore) is offered only after a real exchange; "Save &
  // finish later" is always available so the founder is never stuck (Task 1.2).
  const wrapUpEnabled = canWrapUp(messages.length, sessionId, complete.isPending);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || send.isPending) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    send.mutate({ content: trimmed });
    setInputValue('');
    Keyboard.dismiss();
  }, [inputValue, send]);

  // Voice seam: drop the transcript into the composer for review (88-06).
  const handleTranscript = useCallback((text: string) => {
    setInputValue((prev) => (prev ? `${prev} ${text}` : text));
  }, []);

  // Complete + background rescore, then reveal the before→after payoff. This is
  // the rescore path — it ALWAYS runs on confirm, independent of what (if
  // anything) the founder chose to publish (spec §1 decoupling).
  const runCompleteAndReveal = useCallback(() => {
    // Show the reveal's "Recalculating" IMMEDIATELY (not after complete resolves)
    // so there is no chat flash between publish and the rescore screen — complete
    // now re-derives evidence from the full transcript (an LLM pass), so awaiting
    // it before revealing dropped the founder back to the chat for several seconds.
    // The reveal polls the session for score_event_after; complete runs underneath.
    setReviewing(false);
    setWrappedUp(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
    complete.mutate(undefined, {
      // If completion itself fails there is no rescore to await — fall back to the
      // chat rather than hang on "Recalculating".
      onError: () => setWrappedUp(false),
    });
  }, [complete]);

  // Kick off (or retry) digest generation for the review overlay. On success the
  // review shows the editable digest; on failure `digestData` stays null and the
  // overlay offers a retry or "finish without digest" (digest is additive, never
  // a dead-end — spec §5).
  const digestMutate = digest.mutate;
  const triggerDigest = useCallback(() => {
    digestMutate(undefined, { onSuccess: (data) => setDigestData(data) });
  }, [digestMutate]);

  // "Wrap up" (header action) now opens the digest review instead of completing
  // blindly: generate the digest → review + selectively publish → confirm runs
  // complete + rescore.
  const handleWrapUp = useCallback(() => {
    if (!wrapUpEnabled) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
    setDigestData(null);
    setReviewing(true);
    triggerDigest();
  }, [wrapUpEnabled, triggerDigest]);

  // Save & finish later: leave WITHOUT completing. The session stays `active`
  // server-side (start-on-mount transparently resumes it next visit), so no data
  // is lost and there is no dead-end. No reset — completion is not happening here.
  const handleFinishLater = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    onClose?.();
  }, [onClose]);

  const handleDone = useCallback(() => {
    setWrappedUp(false);
    setReviewing(false);
    setDigestData(null);
    reset();
    onClose?.();
  }, [reset, onClose]);

  // "Continue interview" from the reveal (partial wrap-up): close the overlays and
  // re-run start — handleStart RE-OPENS the just-completed (not-fully-covered)
  // session and returns its transcript, so the founder picks up where they left
  // off and the agent keeps probing the areas still to cover. Same sessionId.
  const handleContinue = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    setWrappedUp(false);
    setReviewing(false);
    setDigestData(null);
    startMutate({ startupId });
  }, [startMutate, startupId]);

  // Dimensions still 'not_yet' — drives the reveal's "Continue interview" CTA.
  const stillToCover = dimensionCoverage.filter((d) => d.state === 'not_yet').length;

  // The header "Wrap up" reflects BOTH the digest-generation and completion
  // in-flight states so it never looks idle while work is happening under the
  // review overlay.
  const wrapUpBusy = complete.isPending || (reviewing && digest.isPending);

  // Auto-scroll to the newest turn (and while the mentor is thinking).
  useEffect(() => {
    const t = setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 180);
    return () => clearTimeout(t);
  }, [messages.length, isThinking]);

  // When the keyboard opens on mobile, keep the latest turn (and the composer
  // with the just-transcribed text) visible above it (Task 1.4). Web has no soft
  // keyboard, so this is a no-op there.
  useEffect(() => {
    if (isWeb) return;
    const sub = Keyboard.addListener('keyboardDidShow', () => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
    return () => sub.remove();
  }, []);

  const startError = start.isError ? (start.error as Error) : null;

  return (
    <KeyboardLayout>
      {/* Header row (Task 1.2-R): quiet left "back = save & finish later" and a
          contextual top-right "Wrap up" that only APPEARS once there's a real
          exchange. No exit buttons near the composer. Sits above the progress
          bar → conversation → clean composer. */}
      {!wrappedUp && (
        <InterviewHeader
          title={title}
          startupName={startupName}
          onLeave={handleFinishLater}
          onWrapUp={handleWrapUp}
          showWrapUp={shouldShowWrapUp(messages.length, sessionId, wrapUpBusy)}
          isCompleting={wrapUpBusy}
          colors={colors}
        />
      )}

      {/* Pinned aggregate progress bar — conversation progress, never a score
          (Task 1.3). Bound to the server's blended overallCoverage fraction. */}
      <InterviewProgressBar coverage={overallCoverage} colors={colors} />

      {/* Coverage progress — founder language only, no methodology terms */}
      <InterviewProgress
        coveredTopics={coveredTopics}
        dimensionCoverage={dimensionCoverage}
        isComplete={wrappedUp}
      />

      {/* Conversation */}
      <ScrollView
        ref={scrollRef}
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 20, paddingTop: 16, paddingBottom: 24 }}
        showsVerticalScrollIndicator={false}
        // Auto-scroll to the newest turn the instant the content actually grows
        // (a new answer/reply rendered) — reliable across message sizes, unlike a
        // fixed timer that fires before a long message has laid out (Adi 2026-07-23).
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })}
        keyboardShouldPersistTaps="handled"
        // Swipe the transcript down to dismiss the keyboard (mobile) — keeps it
        // dismissible and lets the founder reveal messages hidden behind it.
        keyboardDismissMode={isWeb ? undefined : 'on-drag'}
      >
        {startError ? (
          <StartError message={getUserMessage(startError)} onRetry={() => startMutate({ startupId })} />
        ) : (
          messages.map((msg, index) => (
            <MessageBubble key={msg.id ?? `msg-${index}`} message={msg} colors={colors} />
          ))
        )}

        {/* Mentor typing indicator between turns */}
        {isThinking && messages[messages.length - 1]?.role !== 'assistant' && (
          <TypingIndicator colors={colors} />
        )}
        {isThinking && messages.length === 0 && <TypingIndicator colors={colors} />}
      </ScrollView>

      {/* Composer */}
      <Composer
        value={inputValue}
        onChangeText={setInputValue}
        onSubmit={handleSend}
        disabled={isThinking || wrappedUp || !sessionId}
        startupName={startupName}
        colors={colors}
        voiceControl={
          // Voice ships dark until DPO sign-off (see VOICE_ENABLED above). When
          // off, no mic renders and text is the only input path.
          !VOICE_ENABLED ? null : renderVoiceControl ? (
            renderVoiceControl({ onTranscript: handleTranscript, disabled: isThinking || wrappedUp })
          ) : (
            // Default: hold-to-speak mic → Voxtral → editable transcript in the
            // composer (88-06). Text stays a full fallback; the mic is additive.
            <InterviewVoiceButton
              onTranscript={handleTranscript}
              disabled={isThinking || wrappedUp || !sessionId}
              sessionId={sessionId ?? undefined}
            />
          )
        }
      />

      {/* === DIGEST REVIEW OVERLAY (Task 2.3) ===
          Wrap up → generate digest → review + selectively publish → confirm runs
          complete + rescore. If digest generation fails, the founder can retry or
          finish without it (digest is additive, never a dead-end). */}
      {reviewing && sessionId && (
        <Animated.View
          entering={FadeIn.duration(220)}
          className="absolute inset-0"
          style={{ backgroundColor: colors.surface.primary }}
        >
          {digestData ? (
            <InterviewDigestReview
              sessionId={sessionId}
              startupId={startupId}
              digest={digestData}
              dimensionCoverage={dimensionCoverage}
              onConfirmed={runCompleteAndReveal}
              onSkip={runCompleteAndReveal}
            />
          ) : digest.isError ? (
            <DigestUnavailable
              message={getUserMessage(digest.error as Error)}
              onRetry={triggerDigest}
              onContinue={runCompleteAndReveal}
              isRetrying={digest.isPending}
              colors={colors}
            />
          ) : (
            <DigestGenerating startupName={startupName} colors={colors} />
          )}
        </Animated.View>
      )}

      {/* === COMPLETION OVERLAY (88-05) === */}
      {wrappedUp && sessionId && (
        <Animated.View
          entering={FadeIn.duration(220)}
          className="absolute inset-0"
          style={{ backgroundColor: colors.surface.primary }}
        >
          {renderCompletion ? (
            renderCompletion({ sessionId, onDone: handleDone })
          ) : (
            // Default: the real before→after uplift reveal (88-05). It mounts
            // `useInterviewSession(sessionId)` internally, which STARTS the
            // background-rescore poll and gates the reveal on `isAwaitingRescore`.
            <InterviewCompletion
              sessionId={sessionId}
              startupName={startupName}
              onDone={handleDone}
              onContinue={handleContinue}
              stillToCover={stillToCover}
            />
          )}
        </Animated.View>
      )}
    </KeyboardLayout>
  );
}

// =============================================================================
// Keyboard layout — keep the composer + transcript above the soft keyboard
// =============================================================================

/**
 * Task 1.4: the transcript lands in the composer for review/edit, so the
 * composer must never hide behind the keyboard on mobile.
 *
 * Uses react-native-keyboard-controller's `KeyboardAvoidingView` with
 * `behavior="padding"` — consistent on iOS AND Android (RN's own only implements
 * `padding` on iOS, leaving Android to slide under the keyboard). The flex
 * ScrollView shrinks and the composer sits directly above the keyboard, so
 * composed/transcribed text stays visible, scrollable, and editable. No
 * hand-rolled offset math.
 *
 * Web has no soft keyboard (and `KeyboardProvider` is native-only, mounted in
 * `_layout.tsx`), so web renders a plain flex container — unaffected.
 */
function KeyboardLayout({ children }: { children: React.ReactNode }) {
  if (isWeb) {
    return <View style={{ flex: 1 }}>{children}</View>;
  }
  // Use React Native's own KeyboardAvoidingView (behavior padding on iOS, height
  // on Android) — the SAME proven pattern as the working DDChatContainer. The
  // keyboard-controller `KeyboardAvoidingView` collapsed the flex-1 transcript
  // ScrollView to 0 height on native (composer floated up, messages invisible;
  // UAT 2026-07-21, native-only — web was always fine). The inner flex:1 View
  // establishes the column so the transcript expands.
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flex: 1 }}
    >
      <View style={{ flex: 1 }}>{children}</View>
    </KeyboardAvoidingView>
  );
}

// =============================================================================
// Overall progress bar — pinned, aggregate conversation progress (Task 1.3)
// =============================================================================

type Colors = ReturnType<typeof useThemeColors>;

// =============================================================================
// Header — quiet "leave" (back) + contextual "Wrap up" (Task 1.2-R)
// =============================================================================

/**
 * The interview's own header row. Owns both exit affordances so they live with
 * the interview state (`useInterview`) instead of being wired through the screen
 * chrome (React Query can't share the container's local session/mutation state):
 *
 *   • LEFT — a quiet back chevron = "save & finish later". The session stays
 *     `active` server-side and transparently resumes next visit; it is NOT a
 *     loud primary button, just a calm way out (Light haptic in the handler).
 *   • RIGHT — a "Wrap up" text action that only APPEARS once `shouldShowWrapUp`
 *     is true (hidden before, not shown-disabled from turn one). It runs
 *     complete + rescore, showing "Wrapping up…" while in flight (Medium haptic).
 *
 * Rendered on BOTH platforms (mobile shows the warm avatar + subtitle; web keeps
 * a compact title), consistent with the in-content header pattern used elsewhere
 * in `(startup)` (e.g. the valuation wizard). The screen's SafeAreaView supplies
 * the top inset on mobile.
 */
const InterviewHeader = memo(function InterviewHeader({
  title,
  startupName,
  onLeave,
  onWrapUp,
  showWrapUp,
  isCompleting,
  colors,
}: {
  title: string;
  startupName?: string;
  onLeave: () => void;
  onWrapUp: () => void;
  showWrapUp: boolean;
  isCompleting: boolean;
  colors: Colors;
}) {
  return (
    <Animated.View
      entering={FadeInDown.delay(80).duration(450)}
      className="flex-row items-center border-b"
      style={{
        backgroundColor: colors.surface.primary,
        borderColor: colors.border.light,
        paddingHorizontal: isWeb ? 20 : 12,
        paddingTop: isWeb ? 14 : 8,
        paddingBottom: isWeb ? 14 : 10,
        gap: 8,
      }}
    >
      {/* Leave = save & finish later (quiet back chevron) */}
      <View style={{ width: 40, height: 40, borderRadius: 20, overflow: 'hidden' }}>
        <Pressable
          onPress={onLeave}
          accessibilityRole="button"
          accessibilityLabel="Save and finish later"
          className="flex-1 items-center justify-center"
        >
          <ChevronLeft size={24} color={colors.text.primary} strokeWidth={2} />
        </Pressable>
      </View>

      {/* Title block — warm avatar + subtitle on mobile, compact on web */}
      {!isWeb ? (
        <View className="flex-1 flex-row items-center" style={{ gap: 10 }}>
          <View
            className="w-9 h-9 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary[500] }}
          >
            <MessagesSquare size={18} color={colors.text.inverse} />
          </View>
          <View className="flex-1">
            <Text
              numberOfLines={1}
              className="text-base font-bold"
              style={{ color: colors.text.primary }}
            >
              {title}
            </Text>
            <Text numberOfLines={1} className="text-xs" style={{ color: colors.text.tertiary }}>
              A relaxed chat about {startupName ?? 'your startup'} — no forms
            </Text>
          </View>
        </View>
      ) : (
        <View className="flex-1">
          <Text
            numberOfLines={1}
            className="text-lg font-bold"
            style={{ color: colors.text.primary }}
          >
            {title}
          </Text>
        </View>
      )}

      {/* Wrap up — appears only when ready (Pressable inside a sized View per
          CLAUDE.md pressable-sizing rule; disabled while completing). */}
      {showWrapUp && (
        <Animated.View entering={FadeIn.duration(200)}>
          <View
            style={{
              height: 36,
              borderRadius: 18,
              overflow: 'hidden',
              backgroundColor: colors.primary[600],
            }}
          >
            <Pressable
              onPress={onWrapUp}
              disabled={isCompleting}
              accessibilityRole="button"
              accessibilityLabel="Wrap up"
              className="flex-1 flex-row items-center justify-center"
              style={{ paddingHorizontal: 14, gap: 6 }}
            >
              <Check size={15} color={colors.text.inverse} strokeWidth={2.5} />
              <Text
                numberOfLines={1}
                className="text-[13px] font-bold"
                style={{ color: colors.text.inverse }}
              >
                {isCompleting ? 'Wrapping up…' : 'Wrap up'}
              </Text>
            </Pressable>
          </View>
        </Animated.View>
      )}
    </Animated.View>
  );
});

/**
 * A single pinned bar reflecting the server's aggregate `overallCoverage`
 * fraction as a percentage. Framed as CONVERSATION progress — never a score,
 * and it never exposes a per-dimension ratio (trade-secret contract). The fill
 * animates (reanimated) toward the measured track width so it reads smoothly as
 * the founder answers.
 */
const InterviewProgressBar = memo(function InterviewProgressBar({
  coverage,
  colors,
}: {
  coverage: number | null;
  colors: Colors;
}) {
  const percent = coveragePercent(coverage);
  const [trackWidth, setTrackWidth] = useState(0);
  const fill = useSharedValue(0);

  useEffect(() => {
    fill.value = withTiming((percent / 100) * trackWidth, {
      duration: 600,
      easing: Easing.out(Easing.cubic),
    });
  }, [percent, trackWidth, fill]);

  const fillStyle = useAnimatedStyle(() => ({ width: fill.value }));

  return (
    <View
      className="px-5 pt-3"
      style={{ backgroundColor: colors.surface.primary }}
    >
      <Text
        numberOfLines={1}
        className="text-[11px] font-semibold mb-1.5"
        style={{ color: colors.text.tertiary }}
      >
        {coverageLabel(coverage)}
      </Text>
      <View
        onLayout={(e) => setTrackWidth(e.nativeEvent.layout.width)}
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: colors.surface.tertiary }}
      >
        <Animated.View
          style={[
            { height: '100%', borderRadius: 999, backgroundColor: colors.primary[600] },
            fillStyle,
          ]}
        />
      </View>
    </View>
  );
});

// =============================================================================
// Message bubble — warm mentor vs. founder, with light markdown for prose
// =============================================================================

const MessageBubble = memo(function MessageBubble({
  message,
  colors,
}: {
  message: InterviewMessageType;
  colors: Colors;
}) {
  const isFounder = message.role === 'user';

  return (
    <Animated.View
      entering={FadeInUp.duration(280)}
      className={cn('mb-3.5', isFounder ? 'items-end' : 'items-start')}
    >
      {!isFounder && (
        <View className="flex-row items-center mb-1.5 ml-1">
          <View
            className="w-5 h-5 rounded-full items-center justify-center mr-1.5"
            style={{ backgroundColor: colors.primary[500] }}
          >
            <Sparkles size={11} color={colors.text.inverse} />
          </View>
          <Text className="text-[11px] font-semibold" style={{ color: colors.text.tertiary }}>
            Your Inverio guide
          </Text>
        </View>
      )}
      <View
        className={cn(
          'px-4 py-3 rounded-2xl',
          isFounder ? 'rounded-br-md' : 'rounded-bl-md'
        )}
        style={{
          maxWidth: isWeb ? '72%' : '86%',
          backgroundColor: isFounder ? colors.primary[600] : colors.surface.secondary,
        }}
      >
        <MessageBody
          content={message.content}
          color={isFounder ? colors.text.inverse : colors.text.primary}
        />
      </View>
    </Animated.View>
  );
});

/** Lightweight prose renderer: paragraphs + simple bullet lines. */
function MessageBody({ content, color }: { content: string; color: string }) {
  const paragraphs = content.split('\n').map((l) => l.trim());
  return (
    <View>
      {paragraphs.map((line, i) => {
        if (!line) return null;
        const isBullet = line.startsWith('-') || line.startsWith('•');
        if (isBullet) {
          return (
            <View key={i} className="flex-row mt-1">
              <Text className="text-[15px] mr-2" style={{ color }}>•</Text>
              <Text className="text-[15px] leading-6 flex-1" style={{ color }}>
                {line.replace(/^[-•]\s*/, '')}
              </Text>
            </View>
          );
        }
        return (
          <Text key={i} className={cn('text-[15px] leading-6', i > 0 && 'mt-2')} style={{ color }}>
            {line}
          </Text>
        );
      })}
    </View>
  );
}

// =============================================================================
// Typing indicator — three pulsing dots (reanimated)
// =============================================================================

const TypingIndicator = memo(function TypingIndicator({ colors }: { colors: Colors }) {
  return (
    <Animated.View entering={FadeIn} className="items-start mb-3.5">
      <View className="flex-row items-center mb-1.5 ml-1">
        <View
          className="w-5 h-5 rounded-full items-center justify-center mr-1.5"
          style={{ backgroundColor: colors.primary[500] }}
        >
          <Sparkles size={11} color={colors.text.inverse} />
        </View>
        <Text className="text-[11px] font-semibold" style={{ color: colors.text.tertiary }}>
          Your Inverio guide
        </Text>
      </View>
      <View
        className="flex-row items-center px-4 py-3.5 rounded-2xl rounded-bl-md"
        style={{ backgroundColor: colors.surface.secondary, gap: 6 }}
      >
        <TypingDot colors={colors} delay={0} />
        <TypingDot colors={colors} delay={160} />
        <TypingDot colors={colors} delay={320} />
      </View>
    </Animated.View>
  );
});

function TypingDot({ colors, delay }: { colors: Colors; delay: number }) {
  const progress = useSharedValue(0);
  useEffect(() => {
    const t = setTimeout(() => {
      progress.value = withRepeat(
        withTiming(1, { duration: 600, easing: Easing.inOut(Easing.ease) }),
        -1,
        true
      );
    }, delay);
    return () => clearTimeout(t);
  }, [delay, progress]);

  const style = useAnimatedStyle(() => ({
    opacity: 0.35 + progress.value * 0.65,
    transform: [{ translateY: -progress.value * 3 }],
  }));

  return (
    <Animated.View
      style={[{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.primary[500] }, style]}
    />
  );
}

// =============================================================================
// Composer — thumb-zone input with voice + send
// =============================================================================

function Composer({
  value,
  onChangeText,
  onSubmit,
  disabled,
  startupName,
  colors,
  voiceControl,
}: {
  value: string;
  onChangeText: (t: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  startupName?: string;
  colors: Colors;
  voiceControl?: React.ReactNode;
}) {
  const canSend = value.trim().length > 0 && !disabled;
  // The composer owns its own bottom safe-area inset (home indicator): the
  // screen's SafeAreaView is now top-only, and KeyboardLayout lifts the whole
  // composer above the keyboard — so this padding keeps the mic/send buttons
  // off the home indicator when the keyboard is DOWN, without double-counting.
  const insets = useSafeAreaInsets();

  const sendStyle = useAnimatedStyle(() => ({
    transform: [{ scale: withSpring(canSend ? 1 : 0.9, { damping: 15, stiffness: 200 }) }],
    opacity: withSpring(canSend ? 1 : 0.5),
  }), [canSend]);

  return (
    <View
      className="px-4 pt-2.5 border-t"
      style={{
        backgroundColor: colors.surface.primary,
        borderColor: colors.border.light,
        paddingBottom: isWeb ? 12 : Math.max(insets.bottom, 8),
      }}
    >
      <View className="flex-row items-end" style={{ gap: 8 }}>
        {voiceControl}
        <View
          className="flex-1 rounded-2xl border"
          style={{ backgroundColor: colors.surface.secondary, borderColor: colors.border.light }}
        >
          <TextInput
            value={value}
            onChangeText={onChangeText}
            onSubmitEditing={onSubmit}
            placeholder={
              startupName ? `Tell me about ${startupName}…` : 'Type your answer…'
            }
            placeholderTextColor={colors.text.tertiary}
            multiline
            maxLength={2000}
            editable={!disabled}
            returnKeyType="send"
            blurOnSubmit={false}
            className="px-4 py-3 text-[15px] max-h-32"
            style={{ color: colors.text.primary, textAlignVertical: 'center' }}
          />
        </View>
        <View style={{ marginBottom: 2 }}>
          <Animated.View style={sendStyle}>
            <Pressable
              onPress={onSubmit}
              disabled={!canSend}
              className="w-11 h-11 rounded-full items-center justify-center"
              style={{ backgroundColor: canSend ? colors.primary[600] : colors.surface.tertiary }}
            >
              <Send size={18} color={canSend ? colors.text.inverse : colors.text.tertiary} style={{ marginLeft: 2 }} />
            </Pressable>
          </Animated.View>
        </View>
      </View>
    </View>
  );
}

// =============================================================================
// Start error + default completion
// =============================================================================

function StartError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const colors = useThemeColors();
  return (
    <Animated.View
      entering={FadeIn}
      className="rounded-2xl p-4 mt-6"
      style={{ backgroundColor: colors.red.light }}
    >
      <Text className="text-sm mb-3" style={{ color: colors.error }}>
        {message}
      </Text>
      <Pressable
        onPress={onRetry}
        className="flex-row items-center self-start rounded-xl px-4 py-2"
        style={{ backgroundColor: colors.primary[600] }}
      >
        <RotateCcw size={14} color={colors.text.inverse} />
        <Text className="text-sm font-semibold ml-2" style={{ color: colors.text.inverse }}>
          Try again
        </Text>
      </Pressable>
    </Animated.View>
  );
}

// =============================================================================
// Digest review overlay states — generating + unavailable (Task 2.3)
// =============================================================================

/** Shown while the wrap-up digest is being generated (before review). */
function DigestGenerating({ startupName, colors }: { startupName?: string; colors: Colors }) {
  return (
    <View
      className="flex-1 items-center justify-center px-8"
      style={{ backgroundColor: colors.surface.primary }}
    >
      <View
        className="w-20 h-20 rounded-full items-center justify-center mb-6"
        style={{ backgroundColor: colors.primary[50] }}
      >
        <ActivityIndicator size="large" color={colors.primary[600]} />
      </View>
      <Text className="text-xl font-bold text-center mb-2" style={{ color: colors.text.primary }}>
        Putting your story together
      </Text>
      <Text
        className="text-sm text-center leading-relaxed"
        style={{ color: colors.text.tertiary, maxWidth: 320 }}
      >
        We're turning what you shared about {startupName ?? 'your startup'} into a factual
        recap you can review — one moment.
      </Text>
    </View>
  );
}

/**
 * Shown when digest generation fails. The digest is ADDITIVE, so this is never a
 * dead-end: the founder can retry, or finish (complete + rescore) without it.
 */
function DigestUnavailable({
  message,
  onRetry,
  onContinue,
  isRetrying,
  colors,
}: {
  message: string;
  onRetry: () => void;
  onContinue: () => void;
  isRetrying: boolean;
  colors: Colors;
}) {
  return (
    <View
      className="flex-1 items-center justify-center px-8"
      style={{ backgroundColor: colors.surface.primary }}
    >
      <Text className="text-xl font-bold text-center mb-2" style={{ color: colors.text.primary }}>
        Couldn't build your recap
      </Text>
      <Text
        className="text-sm text-center leading-relaxed mb-1"
        style={{ color: colors.text.tertiary, maxWidth: 320 }}
      >
        {message}
      </Text>
      <Text
        className="text-[13px] text-center leading-relaxed mb-7"
        style={{ color: colors.text.tertiary, maxWidth: 320 }}
      >
        No problem — you can try again, or finish now. Your score still recalculates
        either way.
      </Text>

      <View style={{ height: 52, width: '100%', maxWidth: 340, borderRadius: 14, overflow: 'hidden', backgroundColor: colors.primary[600], opacity: isRetrying ? 0.7 : 1 }}>
        <Pressable
          onPress={onRetry}
          disabled={isRetrying}
          accessibilityRole="button"
          accessibilityLabel="Try building the recap again"
          className="flex-1 flex-row items-center justify-center"
          style={{ gap: 8 }}
        >
          <RotateCcw size={17} color={colors.text.inverse} />
          <Text className="text-[15px] font-bold" style={{ color: colors.text.inverse }}>
            {isRetrying ? 'Trying again…' : 'Try again'}
          </Text>
        </Pressable>
      </View>

      <Pressable
        onPress={onContinue}
        disabled={isRetrying}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel="Finish without the recap"
        className="mt-4 px-6 py-2.5"
      >
        <Text className="text-sm font-semibold" style={{ color: colors.text.tertiary }}>
          Finish without it
        </Text>
      </Pressable>
    </View>
  );
}

export const InterviewContainer = memo(InterviewContainerComponent);
